import { useEffect, useRef, useState } from "react";

export default function AccessSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="access"
      aria-labelledby="access-heading"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-stone-500 text-xs tracking-[0.3em] mb-4">ACCESS</p>
          <h2
            id="access-heading"
            className="font-serif text-3xl md:text-4xl text-stone-900 mb-4"
          >
            アクセス
          </h2>
          <p className="text-stone-500 text-sm md:text-base max-w-lg mx-auto">
            伊豆稲取駅より徒歩15分。海沿いの散策道を歩いてお越しください。
          </p>
        </div>

        {/* Map */}
        <div
          className={`rounded-2xl overflow-hidden mb-16 transition-all duration-700 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3270.1584411164226!2d139.0417!3d34.8167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDQ5JzAwLjEiTiAxMzkKwzAyJzMwLjEiRQ!5e0!3m2!1sja!2sjp!4v1600000000000!5m2!1sja!2sjp"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="温泉民宿宝栄丸の地図"
          />
        </div>

        {/* Info grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 transition-all duration-700 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {/* Address */}
          <div className="bg-warm-50 rounded-2xl p-6 md:p-8">
            <h3 className="font-serif text-xl md:text-2xl text-stone-900 mb-6">
              住所・連絡先
            </h3>
            <address className="space-y-4 text-stone-600 text-sm md:text-base not-italic">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-warm-600 text-lg mt-0.5 w-5 h-5 flex items-center justify-center" />
                <div>
                  <p className="text-stone-900 font-medium">住所</p>
                  <p>〒413-0411</p>
                  <p>静岡県賀茂郡東伊豆町稲取965</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-phone-line text-warm-600 text-lg mt-0.5 w-5 h-5 flex items-center justify-center" />
                <div>
                  <p className="text-stone-900 font-medium">電話番号</p>
                  <p>
                    <a
                      href="tel:0557-95-1813"
                      className="hover:text-warm-700 transition-colors"
                    >
                      0557-95-1813
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-time-line text-warm-600 text-lg mt-0.5 w-5 h-5 flex items-center justify-center" />
                <div>
                  <p className="text-stone-900 font-medium">営業時間</p>
                  <p>チェックイン 15:00〜</p>
                  <p>チェックアウト 〜10:00</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-calendar-line text-warm-600 text-lg mt-0.5 w-5 h-5 flex items-center justify-center" />
                <div>
                  <p className="text-stone-900 font-medium">定休日</p>
                  <p>年中無休</p>
                </div>
              </div>
            </address>
          </div>

          {/* Access by train */}
          <div className="bg-warm-50 rounded-2xl p-6 md:p-8">
            <h3 className="font-serif text-xl md:text-2xl text-stone-900 mb-6">
              交通アクセス
            </h3>
            <div className="space-y-6">
              <div>
                <p className="text-stone-900 font-medium text-sm md:text-base mb-2">
                  <i className="ri-train-line mr-2 w-5 h-5 flex items-center justify-center inline-block" />
                  電車でお越しの場合
                </p>
                <p className="text-stone-600 text-sm leading-relaxed">
                  伊豆急行線 伊豆稲取駅より徒歩15分
                  <br />
                  駅から海沿いの道を南に進むと、宝栄丸の看板が見えます。
                </p>
              </div>
              <div>
                <p className="text-stone-900 font-medium text-sm md:text-base mb-2">
                  <i className="ri-car-line mr-2 w-5 h-5 flex items-center justify-center inline-block" />
                  お車でお越しの場合
                </p>
                <p className="text-stone-600 text-sm leading-relaxed">
                  東名高速 厚木ICより約2時間
                  <br />
                  駐車場完備（無料）
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}